import { type ReactNode, useCallback, useId, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import cx from 'classnames'
import styles from './ErrorPage.module.css'
import { Icon } from '@/components/Icon'
import { useBodyClassName } from '@/hooks/body-class'
import defaultLogoUrl from '@/assets/misc/img_taxfyle-symbol.svg'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { ChromeThemeLayer, useChromeThemeColor } from '@/features/shell/theming'
import { useCssVariable } from '@/features/shell/theming/use-css-variable'
import { Button } from '@/components/Button'

export interface ErrorPageProps {
  children?: ReactNode
  logoUrl?: string | null
  workspaceName?: string
  testId?: string
}

/**
 * An error page.
 */
export function ErrorPage({
  logoUrl,
  workspaceName,
  children,
  testId,
}: ErrorPageProps) {
  useBodyClassName(styles.body)
  const isSpacious = useIsSpacious()
  useChromeThemeColor(
    useCssVariable(isSpacious ? '--surface-secondary' : '--surface-primary'),
    ChromeThemeLayer.Root,
  )

  return (
    <div className={cx(styles.root)} data-test={testId}>
      <div className={styles.decorativeBar}>
        <img
          className={styles.logo}
          src={logoUrl || defaultLogoUrl}
          alt={`${workspaceName || 'Taxfyle'} logo`}
        />
      </div>
      <div className={styles.container}>
        <div className={styles.box}>{children}</div>
      </div>
    </div>
  )
}

interface ErrorPageColumnsProps {
  children?: ReactNode
}

/**
 * Error Page Columns
 */
export function ErrorPageColumns({ children }: ErrorPageColumnsProps) {
  return <div className={styles.columns}>{children}</div>
}

interface ErrorPageDetailsColumnProps {
  children?: ReactNode
}

/**
 * Error Page Details Column
 */
export function ErrorPageDetailsColumn({
  children,
}: ErrorPageDetailsColumnProps) {
  return <div className={styles.detailsColumn}>{children}</div>
}

interface ErrorPageIllustrationColumnProps {
  children?: ReactNode
}

/**
 * Error Page Illustration Column
 */
export function ErrorPageIllustrationColumn({
  children,
}: ErrorPageIllustrationColumnProps) {
  return <div className={styles.illustrationColumn}>{children}</div>
}

interface ErrorPageKickerProps {
  children?: ReactNode
}

/**
 * Error Page Kicker
 */
export function ErrorPageKicker({ children }: ErrorPageKickerProps) {
  return <p className={styles.kicker}>{children}</p>
}

interface ErrorPageTitleProps {
  children?: ReactNode
}

/**
 * Error Page Title
 */
export function ErrorPageTitle({ children }: ErrorPageTitleProps) {
  return <h1 className={styles.title}>{children}</h1>
}

interface ErrorPageDescriptionProps {
  children?: ReactNode
}

/**
 * Error Page Description
 */
export function ErrorPageDescription({ children }: ErrorPageDescriptionProps) {
  return <div className={styles.description}>{children}</div>
}

interface ErrorPageActionsProps {
  children?: ReactNode
}

/**
 * Error Page Actions
 */
export function ErrorPageActions({ children }: ErrorPageActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

interface ErrorPageDetailsProps {
  errorText?: string
}

/**
 * Error Page Details
 */
export function ErrorPageDetails({ errorText }: ErrorPageDetailsProps) {
  const contentId = useId()
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = useCallback(() => setExpanded((e) => !e), [])

  const copyToClipboard = useCallback(async () => {
    if (!errorText) {
      return
    }

    try {
      await navigator.clipboard.writeText(errorText)
    } catch (err: unknown) {
      console.error('Unable to copy to clipboard', err)
      alert('Unable to copy to clipboard')
    }
  }, [errorText])

  // Only show details if we have any details at all.
  if (!errorText) {
    return null
  }

  return (
    <aside className={styles.details}>
      <AriaButton
        aria-controls={contentId}
        aria-expanded={expanded}
        onPress={toggleExpanded}
        className={({ isFocusVisible }) =>
          cx(styles.detailsButton, isFocusVisible && styles.focusVisible)
        }
      >
        <span className={styles.detailsButtonText}>Details</span>
        <span className={styles.detailsButtonIcon}>
          <Icon
            name="chevron"
            variant="secondary"
            rotate={expanded ? -90 : 90}
          />
        </span>
      </AriaButton>

      <pre
        id={contentId}
        className={cx(styles.detailsContent, !expanded && styles.hidden)}
        aria-expanded={expanded}
      >
        {errorText}
      </pre>

      {expanded && (
        <span className={styles.detailsCopyButtonContainer}>
          <Button
            variant="text"
            iconLeft="copy"
            label="Copy code"
            onPress={copyToClipboard}
          />
        </span>
      )}
    </aside>
  )
}
